import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  buildStxEtxAsciiLenPacket,
  convertEn,
  getFormattedDate,
  INT_MAX,
  parseSpSnType1Step2,
  startReading,
  startSerialRxMonitor,
  stopSerialRxMonitor,
} from "../utils/util";
import { useCanWrite } from "../utils/useCanWrite";
import {
  Box,
  ActionIcon,
  Button,
  Flex,
  Paper,
  Text,
  TextInput,
  Badge,
  Modal,
  SegmentedControl,
  Select,
} from "@mantine/core";
import {
  IconChevronLeft,
  IconChevronRight,
  IconSearch,
  IconAlertTriangle,
} from "@tabler/icons-react";
import { toast } from "react-toastify";
import axiosInstance from "../axiosInstance";
import classes from "./ScsKeyInject.module.css";

type SendTarget = "serial" | "mac";

const PENDING_PREVIEW_COUNT = 6;

interface TerminalInjectInfo {
  terminalId: number;
  serialNo: string | null;
  spSerialNo: string;
  macAddress: string | null;
  internalModelName: string | null;
  externalModelName: string | null;
  spSerialInjectionYn: string;
  macInjectionYn: string;
}

/** GET /api/v1/terminal/list 응답 행 */
interface TerminalListRow {
  terminalId: number;
  serialNo?: string | null;
  spSerialNo: string;
  macAddress?: string | null;
  internalModelName?: string | null;
  externalModelName?: string | null;
  spSerialInjectionYn?: string;
  macInjectionYn?: string;
}

interface TerminalGroupOption {
  terminalGroupId: number;
  aliasName: string;
}

const mapListRowToInjectInfo = (row: TerminalListRow): TerminalInjectInfo => ({
  terminalId: row.terminalId,
  serialNo: row.serialNo ?? null,
  spSerialNo: row.spSerialNo ?? "",
  macAddress: row.macAddress ?? null,
  internalModelName: row.internalModelName ?? null,
  externalModelName: row.externalModelName ?? null,
  spSerialInjectionYn: row.spSerialInjectionYn ?? "N",
  macInjectionYn: row.macInjectionYn ?? "N",
});

type LogGroupType = "connect" | "serial" | "mac" | "system";
type LogTone = "default" | "ok" | "err" | "detail";

interface LogLine {
  time: string;
  text: string;
  tone: LogTone;
}

interface LogGroup {
  id: number;
  type: LogGroupType;
  title: string;
  time: string;
  lines: LogLine[];
}

const GROUP_STYLE: Record<
  LogGroupType,
  { border: string; bg: string; titleColor: string }
> = {
  connect: { border: "#3b82f6", bg: "#eff6ff", titleColor: "#1d4ed8" },
  serial: { border: "#0d9488", bg: "#f0fdfa", titleColor: "#0f766e" },
  mac: { border: "#7c3aed", bg: "#f5f3ff", titleColor: "#6d28d9" },
  system: { border: "#94a3b8", bg: "#f8fafc", titleColor: "#475569" },
};

const bytesToHex = (bytes: number[] | Uint8Array): string =>
  Array.from(bytes)
    .map((b) => b.toString(16).padStart(2, "0"))
    .join(" ");

const isInjected = (yn: string | null | undefined) =>
  (yn ?? "").trim().toUpperCase() === "Y";

const isInjectWorkComplete = (row: {
  spSerialInjectionYn?: string;
  macInjectionYn?: string;
}) => isInjected(row.spSerialInjectionYn) && isInjected(row.macInjectionYn);

/** 단말 주입 순서: 맥(2번) → 시리얼(3번) */
const resolveNextInjectType = (
  row: Pick<TerminalInjectInfo, "spSerialInjectionYn" | "macInjectionYn">,
): SendTarget | null => {
  if (!isInjected(row.macInjectionYn)) return "mac";
  if (!isInjected(row.spSerialInjectionYn)) return "serial";
  return null;
};

/** 조회 기준 S/N(앵커) + 범위 목록 (앵커 중복 제거) — 배지·이동용 */
const buildDisplayQueue = (
  anchor: TerminalListRow | null,
  rangeList: TerminalListRow[],
  anchorSn: string,
): TerminalListRow[] => {
  const sn = anchorSn.trim();
  const out: TerminalListRow[] = [];
  if (anchor?.spSerialNo?.trim() === sn) {
    out.push(anchor);
  }
  for (const row of rangeList) {
    if (row.spSerialNo !== sn) {
      out.push(row);
    }
  }
  return out;
};

/** 화살표 이동용 (관련 S/N 전체). 자동 다음 주입은 미완료만 사용 */
const buildNavQueue = (
  anchor: TerminalListRow | null,
  rangeList: TerminalListRow[],
  anchorSn: string,
): TerminalListRow[] => buildDisplayQueue(anchor, rangeList, anchorSn);

const countPendingInject = (rows: TerminalListRow[]) =>
  rows.filter((row) => !isInjectWorkComplete(row)).length;

const findNextIncompleteRow = (
  ordered: TerminalListRow[],
  afterSn: string | null,
): TerminalListRow | null => {
  if (!ordered.length) return null;
  const startIdx = afterSn
    ? ordered.findIndex((row) => row.spSerialNo === afterSn)
    : -1;
  for (let i = Math.max(0, startIdx + 1); i < ordered.length; i++) {
    if (!isInjectWorkComplete(ordered[i])) return ordered[i];
  }
  return null;
};

/** 미리보기에 현재 작업대상이 항상 보이도록 */
const sliceDisplayQueue = (
  displayQueue: TerminalListRow[],
  currentWorkSn: string | null,
): TerminalListRow[] => {
  const slice = displayQueue.slice(0, PENDING_PREVIEW_COUNT);
  if (
    currentWorkSn &&
    !slice.some((r) => r.spSerialNo === currentWorkSn)
  ) {
    const current = displayQueue.find((r) => r.spSerialNo === currentWorkSn);
    if (current) {
      return [...slice.slice(0, PENDING_PREVIEW_COUNT - 1), current];
    }
  }
  return slice;
};

const parseSerialSequence = (value: string) => {
  const match = value.trim().match(/^(.*?)(\d+)$/);
  if (!match) return null;
  return {
    prefix: match[1],
    numberText: match[2],
    number: Number(match[2]),
  };
};

const compareSerialSequence = (a: string, b: string) => {
  const parsedA = parseSerialSequence(a);
  const parsedB = parseSerialSequence(b);
  if (
    parsedA &&
    parsedB &&
    parsedA.prefix === parsedB.prefix &&
    parsedA.numberText.length === parsedB.numberText.length
  ) {
    return parsedA.number - parsedB.number;
  }
  return a.localeCompare(b);
};

const sortSerialRows = (rows: TerminalListRow[], descending: boolean) =>
  [...rows].sort((a, b) => {
    const order = compareSerialSequence(a.spSerialNo, b.spSerialNo);
    return descending ? -order : order;
  });

const buildMissingSequenceMessage = (previous: string, current: string) => {
  const parsedPrev = parseSerialSequence(previous);
  const parsedCurrent = parseSerialSequence(current);
  if (
    !parsedPrev ||
    !parsedCurrent ||
    parsedPrev.prefix !== parsedCurrent.prefix ||
    parsedPrev.numberText.length !== parsedCurrent.numberText.length
  ) {
    return null;
  }
  const diff = Math.abs(parsedCurrent.number - parsedPrev.number);
  if (diff <= 1) return null;

  const start = Math.min(parsedPrev.number, parsedCurrent.number) + 1;
  const end = Math.max(parsedPrev.number, parsedCurrent.number) - 1;
  const format = (num: number) =>
    `${parsedPrev.prefix}${String(num).padStart(parsedPrev.numberText.length, "0")}`;

  return start === end
    ? `중간 번호 누락: ${format(start)}`
    : `중간 번호 누락: ${format(start)} ~ ${format(end)}`;
};

const filterRowsByPrefix = (rows: TerminalListRow[], prefix: string) => {
  const p = prefix.trim().toUpperCase();
  if (!p) return rows;
  return rows.filter((row) =>
    (row.spSerialNo || "").toUpperCase().startsWith(p),
  );
};

/** 화면 표시용 MAC (전송값에는 사용하지 않음)
 * - C4E7BE0A5D8E → C4E7BE 0A5D8E (12자리 hex는 6+6)
 * - AAAAAA000006 → AAAAAA 000006 (문자 접두 + 숫자)
 */
const formatMacForDisplay = (mac: string | null | undefined) => {
  const value = (mac ?? "").trim();
  if (!value) return "";
  const compact = value.replace(/[\s:\-]/g, "").toUpperCase();
  if (/^[0-9A-F]{12}$/.test(compact)) {
    return `${compact.slice(0, 6)} ${compact.slice(6)}`;
  }
  const parsed = parseSerialSequence(compact);
  if (parsed?.prefix) {
    return `${parsed.prefix} ${parsed.numberText}`;
  }
  return value;
};

/** 한글 IME 상태에서도 물리 키(영문) 기준으로 문자 추출 */
const physicalCharFromKeyboardEvent = (e: {
  code: string;
  key: string;
  shiftKey: boolean;
}): string | null => {
  const { code } = e;
  if (code.startsWith("Key") && code.length === 4) {
    const letter = code.slice(3);
    return e.shiftKey ? letter.toUpperCase() : letter.toLowerCase();
  }
  if (code.startsWith("Digit") && code.length === 6) {
    const digit = code.slice(5);
    if (!e.shiftKey) return digit;
    const shifted: Record<string, string> = {
      "1": "!",
      "2": "@",
      "3": "#",
      "4": "$",
      "5": "%",
      "6": "^",
      "7": "&",
      "8": "*",
      "9": "(",
      "0": ")",
    };
    return shifted[digit] ?? digit;
  }
  if (code.startsWith("Numpad") && code.length === 7) {
    const digit = code.slice(6);
    if (/^\d$/.test(digit)) return digit;
  }
  if (code === "Minus" || code === "NumpadSubtract") return "-";
  if (code === "Equal") return e.shiftKey ? "+" : "=";
  if (code === "Period" || code === "NumpadDecimal") return ".";
  if (code === "Slash") return e.shiftKey ? "?" : "/";
  // 이미 영문/숫자면 key 사용
  if (e.key.length === 1 && /[A-Za-z0-9\-_.]/.test(e.key)) return e.key;
  return null;
};

/** 바코드 스캔값: 한글 자모 → 영문 QWERTY 복원 후 대문자 */
const normalizeBarcodeScan = (raw: string) =>
  convertEn(raw.replace(/\s/g, "")).trim().toUpperCase();

const drainA1Frames = (
  buffer: number[],
): { frames: number[][]; remaining: number[] } => {
  const frames: number[][] = [];
  let i = 0;
  while (i <= buffer.length - 4) {
    if (
      buffer[i] === 0x02 &&
      buffer[i + 1] === 0xa1 &&
      buffer[i + 2] === 0x03
    ) {
      frames.push(buffer.slice(i, i + 4));
      i += 4;
    } else {
      i += 1;
    }
  }
  const remaining = buffer.slice(i);
  return {
    frames,
    remaining: remaining.length > 32 ? remaining.slice(-32) : remaining,
  };
};

const waitUntilReadableUnlocked = async (
  port: SerialPort,
  timeoutMs = 800,
): Promise<boolean> => {
  const start = Date.now();
  while (Date.now() - start < timeoutMs) {
    if (!port.readable?.locked) return true;
    await new Promise((r) => setTimeout(r, 20));
  }
  return !port.readable?.locked;
};

const ScsKeyInject: React.FC<{ menuTitle: string }> = ({ menuTitle }) => {
  const canWrite = useCanWrite();
  const serialPortRef = useRef<SerialPort | null>(null);
  const serialMonitorReaderRef =
    useRef<ReadableStreamDefaultReader<Uint8Array> | null>(null);
  const serialReceiveRef =
    useRef<ReadableStreamDefaultReader<Uint8Array> | null>(null);
  const abortControllerRef = useRef<AbortController | null>(null);
  const rxBufferRef = useRef<number[]>([]);
  const lastA2SentAtRef = useRef(0);
  /** 동일 A1 대기 구간에서 안내 로그 1회만 */
  const a1AnnouncedRef = useRef(false);
  const logScrollRef = useRef<HTMLDivElement>(null);
  const scanInputRef = useRef<HTMLInputElement>(null);
  const scanBufferRef = useRef("");
  const groupIdRef = useRef(0);
  const activeGroupIdRef = useRef<number | null>(null);

  const [searchSerial, setSearchSerial] = useState("");
  const [selectedTerminalGroupId, setSelectedTerminalGroupId] = useState<
    string | null
  >(null);
  const [terminalGroups, setTerminalGroups] = useState<TerminalGroupOption[]>(
    [],
  );
  const [loading, setLoading] = useState(false);
  const [terminal, setTerminal] = useState<TerminalInjectInfo | null>(null);
  const terminalRef = useRef<TerminalInjectInfo | null>(null);
  const [pendingList, setPendingList] = useState<TerminalListRow[]>([]);
  const [anchorRow, setAnchorRow] = useState<TerminalListRow | null>(null);
  const [queueFrom, setQueueFrom] = useState("");
  const queueFromRef = useRef("");
  const lastCompletedSerialRef = useRef<string | null>(null);
  /** 이미 주입완료된 단말 재주입 시: mac → serial 순서 기억 */
  const reinjectPhaseRef = useRef<"mac" | "serial" | null>(null);

  const [isPortOpen, setIsPortOpen] = useState(false);
  const [a1Listening, setA1Listening] = useState(false);
  const [a1Ready, setA1Ready] = useState(false);
  const [logGroups, setLogGroups] = useState<LogGroup[]>([]);
  const [queueDescending, setQueueDescending] = useState(false);
  const [sequenceGapMessage, setSequenceGapMessage] = useState<string | null>(
    null,
  );
  const [gapModalMessage, setGapModalMessage] = useState<string | null>(null);
  const [reinjectPhase, setReinjectPhase] = useState<"mac" | "serial" | null>(
    null,
  );

  useEffect(() => {
    terminalRef.current = terminal;
  }, [terminal]);

  const activeSendPhase = useMemo((): SendTarget => {
    if (!terminal) return "mac";
    const next = resolveNextInjectType(terminal);
    if (next) return next;
    // 이미 완료된 단말: 재주입 단계 (기본 MAC부터)
    return reinjectPhase === "serial" ? "serial" : "mac";
  }, [reinjectPhase, terminal]);

  const resetReinjectPhase = () => {
    reinjectPhaseRef.current = null;
    setReinjectPhase(null);
  };

  const appendToActiveGroup = (text: string, tone: LogTone = "default") => {
    const gid = activeGroupIdRef.current;
    if (gid == null) return;
    const time = getFormattedDate();
    setLogGroups((prev) =>
      prev.map((g) =>
        g.id === gid ? { ...g, lines: [...g.lines, { time, text, tone }] } : g,
      ),
    );
  };

  const startLogGroup = (type: LogGroupType, title: string) => {
    const id = ++groupIdRef.current;
    activeGroupIdRef.current = id;
    const time = getFormattedDate();
    setLogGroups((prev) => [
      ...prev.slice(-30),
      { id, type, title, time, lines: [] },
    ]);
    return id;
  };

  const endActiveGroup = () => {
    activeGroupIdRef.current = null;
  };

  const logSystem = (text: string, tone: LogTone = "default") => {
    if (activeGroupIdRef.current == null) startLogGroup("system", "시스템");
    appendToActiveGroup(text, tone);
    endActiveGroup();
  };

  useEffect(() => {
    const el = logScrollRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [logGroups]);

  const resetA1Announce = () => {
    a1AnnouncedRef.current = false;
    setA1Ready(false);
  };

  useEffect(() => {
    return () => {
      void closePort();
    };
  }, []);

  useEffect(() => {
    axiosInstance
      .get("/api/v1/terminal/group/list")
      .then((res) => {
        const list = (res.data?.data ?? []) as TerminalGroupOption[];
        setTerminalGroups(Array.isArray(list) ? list : []);
      })
      .catch((err) => {
        console.error(err);
        setTerminalGroups([]);
      });
  }, []);

  /** 바코드 스캐너: 포커스 없이 숨김 입력에 흡수 (Enter 시 조회) */
  useEffect(() => {
    const el = scanInputRef.current;
    if (!el) return;
    const focusScan = () => {
      const active = document.activeElement;
      if (
        active instanceof HTMLInputElement ||
        active instanceof HTMLTextAreaElement
      ) {
        if (active.dataset.scanCapture !== "true") {
          return;
        }
      }
      el.focus({ preventScroll: true });
    };
    focusScan();
    const onBlur = () => window.setTimeout(focusScan, 80);
    el.addEventListener("blur", onBlur);
    return () => el.removeEventListener("blur", onBlur);
  }, []);

  const applyTerminalFromRow = (row: TerminalListRow) => {
    const info = mapListRowToInjectInfo(row);
    if (!info.macAddress?.trim()) {
      toast.warn(`MAC 미등록: ${info.spSerialNo}`);
    }
    resetReinjectPhase();
    setTerminal(info);
    resetA1Announce();
  };

  const syncSearchFieldsFromSerial = (full: string) => {
    setSearchSerial(full.trim().toUpperCase());
  };

  const showSequenceGap = (
    message: string | null,
    options?: { silent?: boolean },
  ) => {
    setSequenceGapMessage(message);
    if (message && !options?.silent) {
      setGapModalMessage(message);
    }
  };

  const loadInjectQueue = async (
    fromSpSerialNo: string,
    options?: { silent?: boolean; advanceToNext?: boolean },
  ) => {
    const q = fromSpSerialNo.trim().toUpperCase();
    const groupIdNum = selectedTerminalGroupId
      ? Number(selectedTerminalGroupId)
      : NaN;
    const hasGroup = Number.isFinite(groupIdNum) && groupIdNum > 0;
    if (!q && !hasGroup) {
      if (!options?.silent) {
        toast.error("별명 또는 S/N을 입력하세요.");
      }
      return;
    }
    if (q) {
      queueFromRef.current = q;
      setQueueFrom(q);
      syncSearchFieldsFromSerial(q);
    }
    setLoading(true);
    resetA1Announce();
    try {
      const queueParams: Record<string, string | number | boolean> = {
        descending: queueDescending,
        currentPageNo: 1,
        recordsPerPage: INT_MAX,
      };
      if (q) {
        queueParams.fromSpSerialNo = q;
      }
      if (hasGroup) {
        queueParams.terminalGroupId = groupIdNum;
      }

      let anchor: TerminalListRow | null = null;
      let list: TerminalListRow[] = [];

      if (q) {
        const [anchorRes, queueRes] = await Promise.all([
          axiosInstance.get("/api/v1/terminal/list", {
            params: { spSerialNo: q, currentPageNo: 1, recordsPerPage: 1 },
          }),
          axiosInstance.get("/api/v1/terminal/inject-queue", {
            params: queueParams,
          }),
        ]);

        const anchorList = (anchorRes.data?.data as { list?: TerminalListRow[] })
          ?.list;
        anchor = anchorList?.[0] ?? null;
        if (!anchor) {
          setTerminal(null);
          setAnchorRow(null);
          setPendingList([]);
          if (!options?.silent) {
            toast.error(`등록되지 않은 S/N: ${q}`);
          }
          return;
        }

        const data = queueRes.data?.data as
          | { list?: TerminalListRow[]; totalCount?: number }
          | undefined;
        const prefix = parseSerialSequence(q)?.prefix ?? "";
        list = filterRowsByPrefix(data?.list ?? [], prefix);

        if (hasGroup && !list.some((row) => row.spSerialNo === q)) {
          setTerminal(null);
          setAnchorRow(null);
          setPendingList([]);
          if (!options?.silent) {
            toast.error(`선택한 별명에 없는 S/N: ${q}`);
          }
          return;
        }
      } else {
        const queueRes = await axiosInstance.get(
          "/api/v1/terminal/inject-queue",
          { params: queueParams },
        );
        const data = queueRes.data?.data as
          | { list?: TerminalListRow[]; totalCount?: number }
          | undefined;
        list = data?.list ?? [];
        const ordered = sortSerialRows(list, queueDescending);
        anchor = ordered[0] ?? null;
        if (!anchor) {
          setTerminal(null);
          setAnchorRow(null);
          setPendingList([]);
          if (!options?.silent) {
            toast.error("선택한 별명에 등록된 단말기가 없습니다.");
          }
          return;
        }
        queueFromRef.current = anchor.spSerialNo;
        setQueueFrom(anchor.spSerialNo);
        syncSearchFieldsFromSerial(anchor.spSerialNo);
      }

      setAnchorRow(anchor);
      const navForWork = buildNavQueue(anchor, list, q || anchor.spSerialNo);
      const orderedNavForWork = sortSerialRows(navForWork, queueDescending);
      const pendingCount = countPendingInject(orderedNavForWork);
      setPendingList(list);

      const advance = options?.advanceToNext ?? false;
      let workRow = anchor;
      if (advance) {
        const completedSn = lastCompletedSerialRef.current;
        workRow =
          findNextIncompleteRow(orderedNavForWork, completedSn) ??
          findNextIncompleteRow(orderedNavForWork, null) ??
          anchor;
      }
      applyTerminalFromRow(workRow);

      const orderedDisplay = orderedNavForWork;
      const workIdx = orderedDisplay.findIndex(
        (row) => row.spSerialNo === workRow.spSerialNo,
      );
      const nextInList =
        workIdx >= 0 ? orderedDisplay[workIdx + 1] : orderedDisplay[0];
      const listGapMessage = nextInList
        ? buildMissingSequenceMessage(workRow.spSerialNo, nextInList.spSerialNo)
        : null;

      if (advance && lastCompletedSerialRef.current) {
        const advanceGapMessage = buildMissingSequenceMessage(
          lastCompletedSerialRef.current,
          workRow.spSerialNo,
        );
        showSequenceGap(advanceGapMessage || listGapMessage, {
          silent: options?.silent,
        });
      } else {
        showSequenceGap(listGapMessage, { silent: options?.silent });
      }

      if (!options?.silent) {
        const lookupLabel = q || "별명";
        const anchorDone = isInjectWorkComplete(anchor);
        if (anchorDone && pendingCount === 0) {
          toast.info(`조회 ${lookupLabel} 주입완료 · 대기 단말 없음`);
        } else if (anchorDone) {
          toast.success(
            `조회 ${lookupLabel} · 주입완료 · 목록 ${orderedNavForWork.length}건 · 대기 ${pendingCount}건 (${
              queueDescending ? "내림차순" : "오름차순"
            })`,
          );
        } else {
          toast.success(
            `조회 ${lookupLabel} · 작업대상 ${workRow.spSerialNo} · 목록 ${orderedNavForWork.length}건 · 대기 ${pendingCount}건 (${
              queueDescending ? "내림차순" : "오름차순"
            })`,
          );
        }
      }
    } catch (e) {
      toast.error("주입 대기 목록 조회 실패");
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  const fetchTerminal = () => {
    const q = searchSerial.trim();
    if (!q && !selectedTerminalGroupId) {
      toast.error("별명 또는 S/N을 입력하세요.");
      return;
    }
    void loadInjectQueue(q ? normalizeBarcodeScan(q) : "", {
      advanceToNext: false,
    });
  };

  const onBarcodeScan = (raw: string) => {
    const v = normalizeBarcodeScan(raw);
    if (!v) return;
    syncSearchFieldsFromSerial(v);
    lastCompletedSerialRef.current = null;
    setSequenceGapMessage(null);
    setGapModalMessage(null);
    void loadInjectQueue(v, { advanceToNext: false });
  };

  const patchInjectionStatus = async (
    type: "serial" | "mac",
    success: boolean,
    term: TerminalInjectInfo,
  ) => {
    const payload: Record<string, unknown> = {
      terminalId: term.terminalId,
      lookupSpSerialNo: term.spSerialNo,
      message:
        type === "serial"
          ? `S/N 주입 ${success ? "성공" : "실패"}(${term.spSerialNo})`
          : `MAC 주입 ${success ? "성공" : "실패"}(${term.macAddress})`,
    };
    if (type === "serial") {
      payload.spSerialInjectionYn = success ? "Y" : "N";
    } else {
      payload.macInjectionYn = success ? "Y" : "N";
    }
    const res = await axiosInstance.patch("/api/v1/terminal/update", payload);
    if (res.data?.code !== "200") {
      throw new Error(res.data?.msg || "서버 갱신 실패");
    }
  };

  const refreshTerminalState = (patch: Partial<TerminalInjectInfo>) => {
    setTerminal((prev) => {
      if (!prev) return prev;
      const next = { ...prev, ...patch };
      resetA1Announce();
      return next;
    });
  };

  const sendA2Payload = async (port: SerialPort, payload: string) => {
    const writer = port.writable?.getWriter();
    if (!writer) throw new Error("쓰기 스트림을 가져올 수 없습니다");
    try {
      const packet = buildStxEtxAsciiLenPacket(0xa2, payload);
      await writer.write(packet);
      appendToActiveGroup(`▶ A2 전송  payload="${payload}"`, "default");
      appendToActiveGroup(`   PACKET ${bytesToHex(packet)}`, "detail");
    } finally {
      writer.releaseLock();
    }
  };

  const tryReadA3 = async (
    port: SerialPort,
    expectPayload: string,
  ): Promise<boolean> => {
    try {
      const unlocked = await waitUntilReadableUnlocked(port, 1000);
      if (!unlocked) {
        appendToActiveGroup("✗ 스트림 lock 해제 타임아웃", "err");
        return false;
      }
      const parser = parseSpSnType1Step2();
      const a3 = await startReading(
        parser,
        port,
        8000,
        serialReceiveRef,
        abortControllerRef,
      );
      const ascii = a3
        .map((b) => (b >= 0x20 && b <= 0x7e ? String.fromCharCode(b) : "."))
        .join("");
      appendToActiveGroup(`◀ A3 수신  "${ascii}"`, "detail");
      if (ascii.includes(expectPayload.replace(/\s/g, ""))) {
        appendToActiveGroup("✓ 확인 일치", "ok");
        return true;
      }
      appendToActiveGroup(`✗ 응답 불일치 (기대: ${expectPayload})`, "err");
      return false;
    } catch {
      appendToActiveGroup("✗ A3 수신 없음/타임아웃", "err");
      return false;
    }
  };

  const runInject = async (
    type: "serial" | "mac",
    a1Frame?: number[],
    workTerminal?: TerminalInjectInfo,
  ): Promise<boolean> => {
    if (!canWrite) {
      toast.error("실행 권한이 없습니다.");
      return false;
    }
    const term = workTerminal ?? terminalRef.current;
    if (!term) {
      toast.error("단말기를 먼저 조회하세요.");
      return false;
    }
    const port = serialPortRef.current;
    if (!port) {
      toast.error("시리얼 포트를 연결하세요.");
      return false;
    }

    const payload =
      type === "serial"
        ? term.spSerialNo
        : (term.macAddress ?? "").replace(/\s/g, "").toUpperCase();

    if (!payload) {
      toast.error(type === "mac" ? "MAC이 없습니다." : "S/N이 없습니다.");
      return false;
    }

    const title =
      type === "serial"
        ? `${payload} · S/N 주입`
        : `${payload} · MAC 주입`;

    startLogGroup(type, title);
    if (a1Frame) {
      appendToActiveGroup(`◀ A1 수신  ${bytesToHex(a1Frame)}`, "default");
    }

    await stopSerialRxMonitor(serialMonitorReaderRef);
    setA1Listening(false);

    try {
      await sendA2Payload(port, payload);
      lastA2SentAtRef.current = Date.now();
      const ok = await tryReadA3(port, payload);
      if (ok) {
        await patchInjectionStatus(type, true, term);
        if (type === "serial") {
          refreshTerminalState({ spSerialInjectionYn: "Y" });
        } else {
          refreshTerminalState({ macInjectionYn: "Y" });
        }
      } else {
        await patchInjectionStatus(type, false, term);
      }
      endActiveGroup();
      return ok;
    } catch (err) {
      appendToActiveGroup(`✗ 전송 실패: ${String(err)}`, "err");
      endActiveGroup();
      return false;
    } finally {
      resetA1Announce();
      if (serialPortRef.current) startA1Monitor(true);
    }
  };

  const handleA1Frame = (frame: number[]) => {
    const workTerminal = terminalRef.current;
    if (!workTerminal) return;
    const now = Date.now();
    if (now - lastA2SentAtRef.current < 400) return;
    if (a1AnnouncedRef.current) return;

    a1AnnouncedRef.current = true;
    setA1Ready(true);

    void (async () => {
      const pendingType = resolveNextInjectType(workTerminal);
      const alreadyComplete = pendingType == null;
      // 이미 완료된 단말도 화면에 떠 있으면 MAC → S/N 순으로 재주입
      const injectType: SendTarget = alreadyComplete
        ? reinjectPhaseRef.current === "serial"
          ? "serial"
          : "mac"
        : pendingType;

      if (injectType === "mac") {
        logSystem(
          alreadyComplete
            ? `A1 수신 — ${workTerminal.spSerialNo} MAC 재주입 전송`
            : `A1 수신 — ${workTerminal.spSerialNo} MAC 자동 전송`,
          "ok",
        );
        const ok = await runInject("mac", frame, workTerminal);
        if (ok && alreadyComplete) {
          reinjectPhaseRef.current = "serial";
          setReinjectPhase("serial");
        }
        toast[ok ? "success" : "error"](
          ok
            ? alreadyComplete
              ? "MAC 재주입 전송 성공"
              : "MAC 자동 전송 성공"
            : alreadyComplete
              ? "MAC 재주입 전송 실패"
              : "MAC 자동 전송 실패",
        );
      } else {
        logSystem(
          alreadyComplete
            ? `A1 수신 — ${workTerminal.spSerialNo} S/N 재주입 전송`
            : `A1 수신 — ${workTerminal.spSerialNo} S/N 자동 전송`,
          "ok",
        );
        const ok = await runInject("serial", frame, workTerminal);
        if (ok) {
          lastCompletedSerialRef.current = workTerminal.spSerialNo;
          resetReinjectPhase();
          toast.success(
            alreadyComplete
              ? "S/N 재주입 전송 성공"
              : "S/N 자동 전송 성공",
          );
          await loadInjectQueue(queueFromRef.current, {
            silent: false,
            advanceToNext: true,
          });
        } else {
          toast.error(
            alreadyComplete
              ? "S/N 재주입 전송 실패"
              : "S/N 자동 전송 실패",
          );
        }
      }
    })();
  };

  const onRxChunk = (bytes: number[]) => {
    rxBufferRef.current.push(...bytes);
    const { frames, remaining } = drainA1Frames(rxBufferRef.current);
    rxBufferRef.current = remaining;
    for (const frame of frames) {
      handleA1Frame(frame);
    }
  };

  const startA1Monitor = (silent = false) => {
    const port = serialPortRef.current;
    if (!port || serialMonitorReaderRef.current) return;
    rxBufferRef.current = [];
    const ok = startSerialRxMonitor(port, onRxChunk, serialMonitorReaderRef);
    if (ok) {
      setA1Listening(true);
      if (!silent) logSystem("A1 감시 시작", "detail");
    }
  };

  const stopA1Monitor = async () => {
    await stopSerialRxMonitor(serialMonitorReaderRef);
    setA1Listening(false);
    resetA1Announce();
  };

  const openPort = async () => {
    try {
      const port = await navigator.serial.requestPort();
      await port.open({ baudRate: 115200 });
      serialPortRef.current = port;
      setIsPortOpen(true);
      resetA1Announce();
      startLogGroup("connect", "시리얼 연결");
      appendToActiveGroup("포트 연결 115200 8N1", "ok");
      endActiveGroup();
      startA1Monitor();
      toast.success("시리얼 연결 · A1 감시 시작");
    } catch (err) {
      toast.error(String(err));
    }
  };

  const closePort = async () => {
    await stopA1Monitor();
    const port = serialPortRef.current;
    if (port) {
      try {
        await port.close();
      } catch {
        /* ignore */
      }
    }
    serialPortRef.current = null;
    setIsPortOpen(false);
    logSystem("포트 연결 해제", "detail");
  };

  const onSendMac = async () => {
    if (!canWrite) {
      toast.error("실행 권한이 없습니다.");
      return;
    }
    if (!a1Ready) {
      toast.error("단말기에서 A1을 먼저 보내주세요.");
      return;
    }
    if (!terminal?.macAddress?.trim()) {
      toast.error("등록된 MAC이 없습니다.");
      return;
    }
    const alreadyComplete = !!terminal && isInjectWorkComplete(terminal);
    if (isInjected(terminal?.macInjectionYn) && !alreadyComplete) {
      toast.warn(
        "이미 MAC 주입 완료 상태입니다. 단말이 맥(2번) 요청인지 확인하세요.",
      );
    }
    const ok = await runInject(
      "mac",
      undefined,
      terminalRef.current ?? undefined,
    );
    if (ok) {
      if (alreadyComplete || isInjected(terminal?.spSerialInjectionYn)) {
        reinjectPhaseRef.current = "serial";
        setReinjectPhase("serial");
      }
      toast.success(alreadyComplete ? "MAC 재주입 전송 성공" : "MAC 전송 성공");
    } else {
      toast.error(alreadyComplete ? "MAC 재주입 전송 실패" : "MAC 전송 실패");
    }
  };

  const onSendSerial = async () => {
    if (!canWrite) {
      toast.error("실행 권한이 없습니다.");
      return;
    }
    if (!a1Ready) {
      toast.error("단말기에서 A1을 먼저 보내주세요.");
      return;
    }
    const alreadyComplete = !!terminal && isInjectWorkComplete(terminal);
    if (isInjected(terminal?.spSerialInjectionYn) && !alreadyComplete) {
      toast.warn(
        "이미 S/N 주입 완료 상태입니다. 단말이 시리얼(3번) 요청인지 확인하세요.",
      );
    }
    const ok = await runInject(
      "serial",
      undefined,
      terminalRef.current ?? undefined,
    );
    if (ok) {
      if (terminalRef.current?.spSerialNo) {
        lastCompletedSerialRef.current = terminalRef.current.spSerialNo;
      }
      resetReinjectPhase();
      toast.success(
        alreadyComplete ? "S/N 재주입 전송 성공" : "S/N 전송 성공",
      );
      await loadInjectQueue(queueFromRef.current, {
        silent: false,
        advanceToNext: true,
      });
    } else {
      toast.error(
        alreadyComplete ? "S/N 재주입 전송 실패" : "S/N 전송 실패",
      );
    }
  };

  const terminalWorkComplete = !!terminal && isInjectWorkComplete(terminal);
  const heroHint = (() => {
    if (!terminal) {
      return { kind: "idle" as const, text: "조회 후 전송할 값 미리보기" };
    }
    if (terminalWorkComplete) {
      const nextIncomplete = findNextIncompleteRow(
        sortSerialRows(
          buildNavQueue(anchorRow, pendingList, queueFrom),
          queueDescending,
        ),
        terminal.spSerialNo,
      );
      return {
        kind: "done" as const,
        text: nextIncomplete
          ? `재주입 가능 · 다음 ${nextIncomplete.spSerialNo}`
          : "재주입 가능",
      };
    }
    const pendingParts: string[] = [];
    if (!isInjected(terminal.spSerialInjectionYn)) pendingParts.push("S/N");
    if (!isInjected(terminal.macInjectionYn)) pendingParts.push("MAC");
    return {
      kind: "work" as const,
      need: pendingParts.length ? pendingParts.join("·") : "주입",
      text: "",
    };
  })();
  const canShowSendButtons = isPortOpen && !!terminal && canWrite;
  const displayQueue = useMemo(
    () =>
      sortSerialRows(
        buildDisplayQueue(anchorRow, pendingList, queueFrom),
        queueDescending,
      ),
    [anchorRow, pendingList, queueDescending, queueFrom],
  );
  const navQueue = useMemo(
    () =>
      sortSerialRows(
        buildNavQueue(anchorRow, pendingList, queueFrom),
        queueDescending,
      ),
    [anchorRow, pendingList, queueDescending, queueFrom],
  );
  const navQueueCount = navQueue.length;
  const pendingWorkCount = useMemo(
    () => countPendingInject(navQueue),
    [navQueue],
  );
  const currentNavIndex = useMemo(() => {
    if (!terminal) return -1;
    return navQueue.findIndex((row) => row.spSerialNo === terminal.spSerialNo);
  }, [navQueue, terminal]);
  const visibleQueue = sliceDisplayQueue(
    displayQueue,
    terminal?.spSerialNo ?? null,
  );
  const nextWorkSn =
    findNextIncompleteRow(navQueue, terminal?.spSerialNo ?? null)?.spSerialNo ??
    null;

  useEffect(() => {
    const currentFrom = queueFromRef.current.trim();
    if (!currentFrom && !selectedTerminalGroupId) return;
    void loadInjectQueue(currentFrom, { silent: true, advanceToNext: false });
  }, [queueDescending]);

  const goToQueueIndex = (index: number) => {
    const row = navQueue[index];
    if (!row) return;
    applyTerminalFromRow(row);
    const next = navQueue[index + 1];
    const gapMessage = next
      ? buildMissingSequenceMessage(row.spSerialNo, next.spSerialNo)
      : null;
    showSequenceGap(gapMessage);
  };

  const goToPrevSerial = () => {
    if (currentNavIndex > 0) goToQueueIndex(currentNavIndex - 1);
  };

  const goToNextSerial = () => {
    if (currentNavIndex >= 0 && currentNavIndex < navQueue.length - 1) {
      goToQueueIndex(currentNavIndex + 1);
    }
  };

  const canGoPrev = currentNavIndex > 0;
  const canGoNext =
    currentNavIndex >= 0 && currentNavIndex < navQueue.length - 1;
  const macDone = !!terminal && isInjected(terminal.macInjectionYn);
  const serialDone = !!terminal && isInjected(terminal.spSerialInjectionYn);

  const queueSummaryText = queueFrom
    ? `${queueDescending ? "내림" : "오름"} · ${navQueueCount}건 · 대기 ${pendingWorkCount}`
    : null;

  const lineColor = (tone: LogTone) => {
    if (tone === "ok") return "#15803d";
    if (tone === "err") return "#dc2626";
    if (tone === "detail") return "#64748b";
    return "#1e293b";
  };

  return (
    <div className={classes.page}>
      <input
        ref={scanInputRef}
        type="text"
        data-scan-capture="true"
        className={classes.scanCapture}
        autoComplete="off"
        autoCorrect="off"
        autoCapitalize="off"
        spellCheck={false}
        lang="en"
        inputMode="text"
        aria-hidden
        tabIndex={-1}
        onKeyDown={(e) => {
          // 한글 IME여도 물리 키코드로 영문 버퍼 구성
          if (e.key === "Enter") {
            e.preventDefault();
            const fromBuf = scanBufferRef.current;
            const fromVal = (e.currentTarget as HTMLInputElement).value;
            scanBufferRef.current = "";
            (e.currentTarget as HTMLInputElement).value = "";
            onBarcodeScan(fromBuf || fromVal);
            return;
          }
          if (e.key === "Backspace") {
            e.preventDefault();
            scanBufferRef.current = scanBufferRef.current.slice(0, -1);
            return;
          }
          const ch = physicalCharFromKeyboardEvent(e);
          if (!ch) return;
          e.preventDefault();
          scanBufferRef.current += ch;
        }}
      />
      <div className={classes.topArea}>
        <Box>
          <Text fz="xl" fw={700}>
            {menuTitle}
          </Text>
        </Box>

        <Paper p="md" withBorder radius="md">
          <Flex gap="sm" align="flex-end" wrap="wrap">
            <Select
              label="별명"
              placeholder="별명 선택 (선택)"
              data={terminalGroups.map((g) => ({
                value: String(g.terminalGroupId),
                label: g.aliasName,
              }))}
              value={selectedTerminalGroupId}
              onChange={setSelectedTerminalGroupId}
              clearable
              searchable
              style={{ minWidth: 180 }}
              size="md"
            />
            <TextInput
              label={
                <Flex
                  justify="space-between"
                  align="center"
                  gap="sm"
                  wrap="wrap"
                  w="100%"
                >
                  <span>S/N 조회</span>
                  {queueSummaryText && (
                    <Text size="xs" c="dimmed" fw={500}>
                      {queueSummaryText}
                    </Text>
                  )}
                </Flex>
              }
              placeholder="바코드 스캔 또는 직접 입력"
              value={searchSerial}
              onChange={(e) =>
                setSearchSerial(e.currentTarget.value.toUpperCase())
              }
              onFocus={() => scanInputRef.current?.blur()}
              onBlur={() => {
                window.setTimeout(
                  () => scanInputRef.current?.focus({ preventScroll: true }),
                  120,
                );
              }}
              style={{ flex: 1, minWidth: 220 }}
              styles={{ label: { width: "100%" } }}
              size="md"
              onKeyDown={(e) => {
                if (e.key === "Enter") void fetchTerminal();
              }}
            />
            <div className={classes.searchActions}>
              <Button
                leftSection={<IconSearch size={18} />}
                onClick={() => void fetchTerminal()}
                loading={loading}
                size="md"
                className={classes.searchActionBtn}
              >
                조회
              </Button>
              <SegmentedControl
                value={queueDescending ? "desc" : "asc"}
                onChange={(value) => {
                  lastCompletedSerialRef.current = null;
                  setSequenceGapMessage(null);
                  setGapModalMessage(null);
                  setQueueDescending(value === "desc");
                }}
                data={[
                  { label: "오름차순", value: "asc" },
                  { label: "내림차순", value: "desc" },
                ]}
                color="blue"
                size="md"
                classNames={{
                  root: classes.sortControl,
                  label: classes.sortControlLabel,
                  control: classes.sortControlItem,
                }}
              />
            </div>
          </Flex>
        </Paper>

        {displayQueue.length > 0 && (
          <Paper p="sm" withBorder radius="md" className={classes.queueCompact}>
            <Flex justify="space-between" align="center" gap="sm" mb={6} wrap="wrap">
              <Text size="sm" fw={600}>
                주입 목록
              </Text>
              <Text size="xs" c="dimmed">
                {navQueueCount}건 · 대기 {pendingWorkCount}건
              </Text>
            </Flex>
            <Flex gap={6} wrap="wrap" align="center">
              {visibleQueue.map((row) => {
                const isCurrent = terminal?.spSerialNo === row.spSerialNo;
                const isLookupAnchor = row.spSerialNo === queueFrom;
                const isNextWork =
                  row.spSerialNo === nextWorkSn && !isCurrent;
                const rowDone = isInjectWorkComplete(row);
                return (
                  <Badge
                    key={row.spSerialNo}
                    size="md"
                    variant={isCurrent || rowDone ? "filled" : "light"}
                    color={
                      rowDone
                        ? "green"
                        : isNextWork || isCurrent
                          ? "teal"
                          : isLookupAnchor
                            ? "orange"
                            : "blue"
                    }
                    style={{ cursor: "pointer" }}
                    onClick={() => applyTerminalFromRow(row)}
                    title={`S/N:${isInjected(row.spSerialInjectionYn) ? "Y" : "N"} MAC:${isInjected(row.macInjectionYn) ? "Y" : "N"}`}
                  >
                    {row.spSerialNo}
                    {isCurrent ? " · 작업" : ""}
                    {!isCurrent && isNextWork ? " · 다음" : ""}
                    {!isCurrent && !isNextWork && rowDone ? " · 완료" : ""}
                  </Badge>
                );
              })}
              {displayQueue.length > visibleQueue.length && (
                <Text size="xs" c="dimmed">
                  +{displayQueue.length - visibleQueue.length}
                </Text>
              )}
            </Flex>
          </Paper>
        )}

        {terminal && (
          <div className={classes.metaBar}>
            <span>
              <em>모델</em>
              {[terminal.internalModelName, terminal.externalModelName]
                .filter(Boolean)
                .join(" / ") || "—"}
            </span>
            <span>
              <em>안드로이드 S/N</em>
              {terminal.serialNo?.trim() || "(미등록)"}
            </span>
          </div>
        )}

        <div className={`${classes.hero} ${!terminal ? classes.heroIdle : ""}`}>
          <div className={classes.heroNavRow}>
            <ActionIcon
              className={classes.heroArrow}
              variant="light"
              color="teal"
              size={48}
              radius="xl"
              onClick={goToPrevSerial}
              disabled={!canGoPrev}
              aria-label="이전 S/N"
            >
              <IconChevronLeft size={32} stroke={2} />
            </ActionIcon>

            <div className={classes.heroMain}>
              <div className={classes.heroDualGrid}>
                <div
                  className={[
                    classes.heroPanel,
                    classes.heroPanelMac,
                    !terminal
                      ? classes.heroPanelIdle
                      : activeSendPhase === "mac"
                        ? classes.heroPanelActive
                        : macDone
                          ? classes.heroPanelDone
                          : classes.heroPanelPending,
                  ].join(" ")}
                >
                  <div className={classes.heroPanelLabel}>MAC</div>
                  <div className={classes.heroPanelValue}>
                    {formatMacForDisplay(terminal?.macAddress) || "—"}
                  </div>
                  <div className={classes.heroPanelStatus}>
                    {!terminal
                      ? "조회 필요"
                      : activeSendPhase === "mac"
                        ? terminalWorkComplete
                          ? "재주입"
                          : "주입 대상"
                        : macDone
                          ? "완료"
                          : "대기"}
                  </div>
                </div>

                <div
                  className={[
                    classes.heroPanel,
                    classes.heroPanelSerial,
                    !terminal
                      ? classes.heroPanelIdle
                      : activeSendPhase === "serial"
                        ? classes.heroPanelActive
                        : serialDone
                          ? classes.heroPanelDone
                          : classes.heroPanelPending,
                  ].join(" ")}
                >
                  <div className={classes.heroPanelLabel}>S/N</div>
                  <div className={classes.heroPanelValue}>
                    {terminal?.spSerialNo || "—"}
                  </div>
                  <div className={classes.heroPanelStatus}>
                    {!terminal
                      ? "조회 필요"
                      : activeSendPhase === "serial"
                        ? terminalWorkComplete
                          ? "재주입"
                          : "주입 대상"
                        : serialDone
                          ? "완료"
                          : "대기"}
                  </div>
                </div>
              </div>

              {terminal && (
                <div className={classes.heroFooter}>
                  {navQueueCount > 0 && currentNavIndex >= 0 && (
                    <Text size="sm" c="dimmed" fw={600}>
                      {currentNavIndex + 1} / {navQueueCount}
                    </Text>
                  )}
                  {sequenceGapMessage && (
                    <Text size="sm" c="red" fw={700}>
                      {sequenceGapMessage}
                    </Text>
                  )}
                  {heroHint.kind === "work" ? (
                    <Badge
                      size="lg"
                      variant="filled"
                      color="red"
                      className={classes.heroNeedBadge}
                    >
                      {heroHint.need} 미완료
                    </Badge>
                  ) : (
                    <Text
                      size="sm"
                      fw={heroHint.kind === "done" ? 700 : 500}
                      c={heroHint.kind === "done" ? "orange" : "dimmed"}
                    >
                      {heroHint.text}
                    </Text>
                  )}
                </div>
              )}
            </div>

            <ActionIcon
              className={classes.heroArrow}
              variant="light"
              color="teal"
              size={48}
              radius="xl"
              onClick={goToNextSerial}
              disabled={!canGoNext}
              aria-label="다음 S/N"
            >
              <IconChevronRight size={32} stroke={2} />
            </ActionIcon>
          </div>
        </div>

        <div
          className={`${classes.a1Banner} ${a1Ready ? classes.a1Ready : classes.a1Wait}`}
        >
          {!isPortOpen
            ? "시리얼 연결 후 A1을 기다립니다"
            : a1Ready
              ? activeSendPhase === "mac"
                ? "A1 수신 — MAC 자동 전송 중"
                : "A1 수신 — S/N 자동 전송 중"
              : activeSendPhase === "mac"
                ? "MAC 전송 대기 — 단말에서 2번을 눌러주세요 (수정예정)"
                : "S/N 전송 대기 — 단말에서 3번을 눌러주세요 (수정예정)"}
        </div>
      </div>

      <Flex className={classes.actionBar} gap="sm" wrap="wrap" align="center">
        <Button
          onClick={() => void openPort()}
          disabled={isPortOpen}
          color="blue"
          size="md"
        >
          시리얼 연결
        </Button>
        <Button
          onClick={() => void closePort()}
          disabled={!isPortOpen}
          variant="default"
          size="md"
        >
          연결 해제
        </Button>
        <Button
          onClick={() => void onSendMac()}
          disabled={!canShowSendButtons}
          color={activeSendPhase === "mac" ? "violet" : "gray"}
          variant={activeSendPhase === "mac" ? "filled" : "default"}
          size="lg"
          fw={700}
          style={{ opacity: activeSendPhase === "mac" ? 1 : 0.5 }}
        >
          MAC 전송
        </Button>
        <Button
          onClick={() => void onSendSerial()}
          disabled={!canShowSendButtons}
          color={activeSendPhase === "serial" ? "teal" : "gray"}
          variant={activeSendPhase === "serial" ? "filled" : "default"}
          size="lg"
          fw={700}
          style={{ opacity: activeSendPhase === "serial" ? 1 : 0.5 }}
        >
          S/N 전송
        </Button>

        <Text size="sm" c={isPortOpen ? "green" : "red"}>
          {isPortOpen
            ? a1Listening
              ? "연결됨 · A1 감시 중"
              : "연결됨"
            : "미연결"}
        </Text>
      </Flex>

      <div className={classes.logSection}>
        <div className={classes.logPanel} ref={logScrollRef}>
          <Text
            size="xs"
            fw={700}
            mb={8}
            style={{
              position: "sticky",
              top: 0,
              background: "#f1f5f9",
              zIndex: 1,
            }}
          >
            통신 로그
          </Text>
          {logGroups.length === 0 ? (
            <Text size="xs" c="dimmed">
              S/N 조회 → 시리얼 연결 → A1 수신 시 MAC 자동 → 다음 A1 시
              S/N 자동
            </Text>
          ) : (
            logGroups.map((group) => {
              const style = GROUP_STYLE[group.type];
              return (
                <Box
                  key={group.id}
                  mb={8}
                  style={{
                    borderLeft: `4px solid ${style.border}`,
                    background: style.bg,
                    borderRadius: 6,
                    padding: "6px 8px",
                  }}
                >
                  <Text size="xs" fw={700} c={style.titleColor}>
                    [{group.time}] {group.title}
                  </Text>
                  {group.lines.map((line, i) => (
                    <div
                      key={i}
                      style={{
                        color: lineColor(line.tone),
                        wordBreak: "break-all",
                        fontSize: line.tone === "detail" ? 11 : 12,
                        paddingLeft: line.tone === "detail" ? 10 : 0,
                      }}
                    >
                      <span style={{ color: "#94a3b8", marginRight: 6 }}>
                        {line.time}
                      </span>
                      {line.text}
                    </div>
                  ))}
                </Box>
              );
            })
          )}
        </div>
      </div>

      <Modal
        opened={!!gapModalMessage}
        onClose={() => setGapModalMessage(null)}
        centered
        size="lg"
        radius="md"
        withCloseButton={false}
        closeOnClickOutside={false}
        closeOnEscape
        classNames={{ content: classes.gapModalContent }}
        title={
          <Flex align="center" gap="sm">
            <IconAlertTriangle size={28} color="#ca8a04" />
            <Text fw={800} size="lg">
              중간 번호 누락
            </Text>
          </Flex>
        }
      >
        <Text className={classes.gapModalMessage} ta="center" my="xl">
          {gapModalMessage}
        </Text>
        <Flex justify="center">
          <Button
            size="lg"
            color="yellow"
            w={160}
            onClick={() => setGapModalMessage(null)}
          >
            확인
          </Button>
        </Flex>
      </Modal>
    </div>
  );
};

export default ScsKeyInject;
